import { useFrame, useThree } from '@react-three/fiber'
import { useMemo, useRef } from 'react'
import * as THREE from 'three'
import { headingVector, rightVector } from '../game/math'
import type { GameRuntime } from '../game/runtime'
import { getCameraTuning, isVisualTeleport } from './scene-contract'

export const CAMERA_MODES = ['chase', 'combat', 'tactical', 'rear', 'hood'] as const
export type GameplayCameraMode = (typeof CAMERA_MODES)[number]
export type CameraMode = GameplayCameraMode | 'showcase'

export function nextCameraMode(mode: GameplayCameraMode): GameplayCameraMode {
  return CAMERA_MODES[(CAMERA_MODES.indexOf(mode) + 1) % CAMERA_MODES.length] ?? 'chase'
}

interface FollowCameraProps {
  runtime: GameRuntime
  mode: CameraMode
  reducedMotion: boolean
}

export function FollowCamera({ runtime, mode, reducedMotion }: FollowCameraProps) {
  const { camera } = useThree()
  const desired = useMemo(() => new THREE.Vector3(), [])
  const desiredTarget = useMemo(() => new THREE.Vector3(), [])
  const smoothedTarget = useMemo(() => new THREE.Vector3(), [])
  const targetReady = useRef(false)
  const cameraRoll = useRef(0)
  const lastPlayerPosition = useRef<{ x: number; y: number } | null>(null)

  useFrame((_, delta) => {
    const state = runtime.getState()
    const player = state.vehicles.find((vehicle) => vehicle.id === state.race.playerId)
    if (!player) return
    const forward = headingVector(player.heading)
    const right = rightVector(player.heading)
    const tuning = getCameraTuning(player.speed, reducedMotion)
    const finished = state.race.phase === 'finished'
    const previousPlayerPosition = lastPlayerPosition.current
    const playerTeleported =
      previousPlayerPosition !== null && isVisualTeleport(previousPlayerPosition, player.position)
    lastPlayerPosition.current = { ...player.position }

    if (finished) {
      const orbit = state.time * 0.18
      desired.set(player.position.x + Math.sin(orbit) * 15, 6.2, player.position.y + Math.cos(orbit) * 15)
      desiredTarget.set(player.position.x, 1.1, player.position.y)
    } else if (mode === 'showcase') {
      desired.set(player.position.x + forward.x * 9.5, 2.75, player.position.y + forward.y * 9.5)
      desiredTarget.set(player.position.x, 1.05, player.position.y)
    } else if (mode === 'hood') {
      desired.set(player.position.x + forward.x * 1.15, 1.7, player.position.y + forward.y * 1.15)
      desiredTarget.set(player.position.x + forward.x * 20, 1.25, player.position.y + forward.y * 20)
    } else if (mode === 'combat') {
      desired.set(
        player.position.x - forward.x * 7.2 + right.x * 2.8,
        3.45,
        player.position.y - forward.y * 7.2 + right.y * 2.8,
      )
      desiredTarget.set(player.position.x + forward.x * 16, 1.2, player.position.y + forward.y * 16)
    } else if (mode === 'tactical') {
      desired.set(player.position.x - forward.x * 12, 13, player.position.y - forward.y * 12)
      desiredTarget.set(player.position.x + forward.x * 4, 0.8, player.position.y + forward.y * 4)
    } else if (mode === 'rear') {
      desired.set(player.position.x + forward.x * 8, 3.7, player.position.y + forward.y * 8)
      desiredTarget.set(player.position.x - forward.x * 10, 1.1, player.position.y - forward.y * 10)
    } else {
      const driftLook = THREE.MathUtils.clamp(player.drift.angle * 2.4, -2.5, 2.5)
      desired.set(
        player.position.x - forward.x * tuning.distance + right.x * driftLook,
        tuning.height,
        player.position.y - forward.y * tuning.distance + right.y * driftLook,
      )
      desiredTarget.set(player.position.x + forward.x * 9, 1.1, player.position.y + forward.y * 9)
    }

    const damping = 1 - Math.exp(-delta * (mode === 'hood' ? 14 : mode === 'showcase' ? 4.5 : 7.5))
    if (!targetReady.current || playerTeleported) {
      smoothedTarget.copy(desiredTarget)
      targetReady.current = true
    } else {
      smoothedTarget.lerp(desiredTarget, 1 - Math.exp(-delta * 12))
    }
    if (playerTeleported) camera.position.copy(desired)
    else camera.position.lerp(desired, damping)
    camera.lookAt(smoothedTarget)
    cameraRoll.current = THREE.MathUtils.lerp(
      cameraRoll.current,
      -player.steer * tuning.rollScale,
      1 - Math.exp(-delta * 7),
    )
    camera.rotation.z += cameraRoll.current
    if (camera instanceof THREE.PerspectiveCamera) {
      const fieldOfView =
        mode === 'hood' ? 68 : mode === 'showcase' ? 48 : mode === 'tactical' ? 66 : mode === 'combat' ? 72 : tuning.fov
      camera.fov = THREE.MathUtils.lerp(camera.fov, fieldOfView, damping)
      camera.updateProjectionMatrix()
    }
  }, -10)

  return null
}
